import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import Cropper from 'react-easy-crop';
import { resolveAssetUrl, toRelativeAssetUrl } from '@/lib/asset';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { 
  LayoutDashboard, 
  DollarSign, 
  Clock, 
  LogOut, 
  Save,
  Plus,
  Trash2,
  Menu,
  X,
  Check,
  AlertCircle,
  Image,
  Upload,
  Loader2,
  Replace,
  Crop,
  Sparkles,
  ExternalLink,
  Edit3,
  Eye,
  EyeOff,
  FileText,
  ArrowUp,
  ArrowDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const brandConfig = {
  luxea: {
    name: "Luxéa Esthétique",
    services: ["cils", "ongles"],
    serviceLabels: {
      cils: "Cils & Sourcils",
      ongles: "Ongles"
    }
  },
  coiffure: {
    name: "Coiffure YT",
    services: ["coiffure"],
    serviceLabels: {
      coiffure: "Coiffure"
    }
  }
};

// Sidebar Component
const Sidebar = ({ brand, activeTab, setActiveTab, onLogout, isMobileOpen, setIsMobileOpen }) => {
  const config = brandConfig[brand] || brandConfig.luxea;
  
  const menuItems = [
    { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
    { id: 'pricing', label: 'Tarifs', icon: DollarSign },
    { id: 'services', label: 'Services', icon: Image },
    { id: 'promotions', label: 'Promotions', icon: Sparkles },
    { id: 'gallery', label: 'Galerie', icon: Image },
    { id: 'hours', label: 'Horaires', icon: Clock },
    { id: 'policies', label: 'Politiques', icon: FileText },
  ];

  return (
    <>
      {isMobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}
      
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-black text-white
        transform transition-transform duration-300
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          <div className="p-6 flex items-center justify-between">
            <h2 className="font-['Cormorant_Garamond'] text-xl font-light">{config.name}</h2>
            <button 
              className="lg:hidden text-white/60 hover:text-white"
              onClick={() => setIsMobileOpen(false)}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <Separator className="bg-white/10" />

          <nav className="flex-1 p-4 space-y-2">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  setIsMobileOpen(false);
                }}
                data-testid={`nav-${item.id}`}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors
                  ${activeTab === item.id 
                    ? 'bg-white text-black' 
                    : 'text-white/70 hover:text-white hover:bg-white/5'
                  }
                `}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            ))}
          </nav>

          <div className="p-4 border-t border-white/10">
            <Link
              to="/"
              className="block w-full text-center text-white/50 hover:text-white text-sm mb-3 transition-colors"
            >
              Voir le site →
            </Link>
            <button
              onClick={onLogout}
              data-testid="logout-button"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              Déconnexion
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Dashboard Tab
const DashboardTab = ({ brand, setActiveTab }) => {
  const config = brandConfig[brand] || brandConfig.luxea;

  const cards = [
    { id: 'pricing',    label: 'Tarifs',           description: 'Gérez les prix de vos services',     icon: DollarSign },
    { id: 'services',   label: 'Services',         description: 'Titres, descriptions et photos de vos services', icon: Image },
    { id: 'promotions', label: 'Promotions',       description: 'Créez et gérez vos affiches promo',  icon: Sparkles },
    { id: 'gallery',    label: 'Galerie',          description: 'Gérez la galerie photos du salon',   icon: Image },
    { id: 'hours',      label: 'Horaires',         description: "Modifiez les heures d'ouverture",    icon: Clock },
    { id: 'policies',   label: 'Politiques',       description: "Politique de rendez-vous et d'annulation", icon: FileText },
  ];

  return (
    <div>
      <h2 className="font-['Cormorant_Garamond'] text-3xl font-light mb-2">Tableau de bord</h2>
      <p className="text-sm text-neutral-500 mb-8">{config.name}</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map((card) => (
          <button
            key={card.id}
            onClick={() => setActiveTab && setActiveTab(card.id)}
            data-testid={`dashboard-card-${card.id}`}
            className="group text-left bg-white border border-neutral-200 hover:border-black p-6 transition-all hover:-translate-y-0.5 hover:shadow-sm"
          >
            <card.icon className="w-8 h-8 text-black mb-4 transition-transform group-hover:scale-110" />
            <h3 className="text-lg font-medium mb-2">{card.label}</h3>
            <p className="text-sm text-neutral-600">{card.description}</p>
            <span className="mt-4 inline-flex items-center text-xs font-medium uppercase tracking-wider text-neutral-500 group-hover:text-black transition-colors">
              Ouvrir →
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

// Pricing Tab
const PricingTab = ({ brand }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchPricing();
  }, [brand]);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/pricing/${brand}`, { withCredentials: true });
      setCategories(response.data.categories || []);
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors du chargement des tarifs' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await axios.put(`${API_URL}/api/admin/pricing/${brand}`, { categories }, { withCredentials: true });
      setMessage({ type: 'success', text: 'Tarifs mis à jour avec succès!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    } finally {
      setSaving(false);
    }
  };

  const updateItem = (catIndex, itemIndex, field, value) => {
    const newCategories = [...categories];
    newCategories[catIndex].items[itemIndex][field] = value;
    setCategories(newCategories);
  };

  const addItem = (catIndex) => {
    const newCategories = [...categories];
    newCategories[catIndex].items.push({ id: Date.now().toString(), name: 'Nouveau service', price: '0' });
    setCategories(newCategories);
  };

  const removeItem = (catIndex, itemIndex) => {
    const newCategories = [...categories];
    newCategories[catIndex].items.splice(itemIndex, 1);
    setCategories(newCategories);
  };

  if (loading) return <div className="text-center py-12">Chargement...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-['Cormorant_Garamond'] text-3xl font-light">Tarifs</h2>
        <Button onClick={handleSave} disabled={saving} className="bg-black text-white hover:bg-neutral-800 rounded-none">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {message.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      <div className="space-y-8">
        {categories.map((category, catIndex) => (
          <div key={category.id || catIndex} className="bg-white border border-neutral-200 p-6">
            <h3 className="font-['Cormorant_Garamond'] text-xl font-medium mb-4 pb-3 border-b border-neutral-200">{category.category}</h3>
            <div className="space-y-3">
              {category.items.map((item, itemIndex) => (
                <div key={item.id || itemIndex} className="flex items-center gap-4">
                  <Input value={item.name} onChange={(e) => updateItem(catIndex, itemIndex, 'name', e.target.value)} className="flex-1 rounded-none" placeholder="Nom du service" />
                  <div className="flex items-center gap-2">
                    <Input value={item.price} onChange={(e) => updateItem(catIndex, itemIndex, 'price', e.target.value)} className="w-24 rounded-none text-right" placeholder="Prix" />
                    <span className="text-neutral-500">$</span>
                  </div>
                  <button onClick={() => removeItem(catIndex, itemIndex)} className="text-red-500 hover:text-red-700 p-2">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <button onClick={() => addItem(catIndex)} className="mt-4 flex items-center gap-2 text-sm text-neutral-500 hover:text-black transition-colors">
              <Plus className="w-4 h-4" />
              Ajouter un service
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Hours Tab
const HoursTab = () => {
  const [hours, setHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => { fetchHours(); }, []);

  const fetchHours = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/hours`, { withCredentials: true });
      setHours(response.data.hours || []);
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors du chargement' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await axios.put(`${API_URL}/api/admin/hours`, { hours }, { withCredentials: true });
      setMessage({ type: 'success', text: 'Horaires mis à jour avec succès!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-12">Chargement...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-['Cormorant_Garamond'] text-3xl font-light">Horaires d'ouverture</h2>
        <Button onClick={handleSave} disabled={saving} className="bg-black text-white hover:bg-neutral-800 rounded-none">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {message.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      <div className="bg-white border border-neutral-200 p-6">
        <div className="space-y-4">
          {hours.map((item, index) => (
            <div key={index} className="flex items-center gap-4">
              <span className="w-32 font-medium">{item.day}</span>
              <Input value={item.hours} onChange={(e) => { const newHours = [...hours]; newHours[index].hours = e.target.value; setHours(newHours); }} className="flex-1 max-w-xs rounded-none" placeholder="Ex: 09h00 - 17h00" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Gallery Tab
const makeId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

// Helper: produce a cropped image blob from an image URL + crop area (in pixels)
const getCroppedBlob = (imageSrc, pixelCrop) => {
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, pixelCrop.width, pixelCrop.height
      );
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error('Canvas vide'));
        resolve(blob);
      }, 'image/jpeg', 0.92);
    };
    image.onerror = () => reject(new Error("Impossible de charger l'image (CORS ?)"));
    image.src = imageSrc;
  });
};

// Crop Modal
const CropModal = ({ imageUrl, onClose, onSave }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [aspect, setAspect] = useState(1); // square by default
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels) return;
    setSaving(true);
    setError('');
    try {
      const blob = await getCroppedBlob(imageUrl, croppedAreaPixels);
      await onSave(blob);
    } catch (e) {
      setError(e.message || 'Erreur lors du recadrage');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" data-testid="crop-modal">
      <div className="bg-white w-full max-w-2xl">
        <div className="flex items-center justify-between p-4 border-b border-neutral-200">
          <h3 className="font-['Cormorant_Garamond'] text-2xl font-light">Recadrer l'image</h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-black" data-testid="crop-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="relative w-full bg-neutral-100" style={{ height: 400 }}>
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="contain"
          />
        </div>

        <div className="p-4 space-y-4 border-t border-neutral-200">
          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-500 w-16">Format :</span>
            <div className="flex gap-2 flex-wrap">
              {[
                { label: '1:1', value: 1 },
                { label: '4:3', value: 4 / 3 },
                { label: '3:4', value: 3 / 4 },
                { label: '16:9', value: 16 / 9 },
                { label: 'Libre', value: null },
              ].map((opt) => (
                <button
                  key={opt.label}
                  onClick={() => setAspect(opt.value || undefined)}
                  className={`px-3 py-1 text-xs border ${aspect === opt.value ? 'bg-black text-white border-black' : 'border-neutral-300 text-neutral-600 hover:border-black'}`}
                  data-testid={`crop-aspect-${opt.label}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-neutral-500 w-16">Zoom :</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1"
              data-testid="crop-zoom-slider"
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> {error}
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button onClick={onClose} variant="outline" className="rounded-none border-black" data-testid="crop-cancel">
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving || !croppedAreaPixels} className="bg-black text-white hover:bg-neutral-800 rounded-none" data-testid="crop-save">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
              {saving ? 'Recadrage...' : 'Enregistrer'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const GalleryTab = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const fileInputRef = useRef(null);

  useEffect(() => { fetchGallery(); }, []);

  // Auto-dismiss success messages after 3 seconds
  useEffect(() => {
    if (message.type === 'success') {
      const t = setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/gallery`, { withCredentials: true });
      const imgs = response.data.images || [];
      // Guarantee unique stable ids for React keys
      const seen = new Set();
      const normalized = imgs.map((img) => {
        let id = img.id;
        if (!id || seen.has(id)) id = makeId();
        seen.add(id);
        return { ...img, id };
      });
      setImages(normalized);
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors du chargement' });
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    // Reset file input value IMMEDIATELY so the same file can be picked again
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    setUploading(true);
    setMessage({ type: '', text: '' });

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/api/admin/gallery/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const uploadedId = response.data.id || makeId();
      const newImage = {
        id: uploadedId,
        url: `/api/files/${response.data.path}`,
        order: images.length
      };
      const nextImages = [...images, newImage];
      setImages(nextImages);
      await axios.put(`${API_URL}/api/admin/gallery`, nextImages, { withCredentials: true });
      setMessage({ type: 'success', text: 'Image ajoutée avec succès!' });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setMessage({
        type: 'error',
        text: typeof detail === 'string' ? detail : "Erreur lors de l'upload"
      });
    } finally {
      setUploading(false);
    }
  };

  const removeImage = async (imageId) => {
    const nextImages = images.filter((img) => img.id !== imageId);
    setImages(nextImages);
    try {
      await axios.put(`${API_URL}/api/admin/gallery`, nextImages, { withCredentials: true });
      setMessage({ type: 'success', text: 'Image supprimée' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la suppression' });
    }
  };

  // Upload a file/blob and return the resulting public URL
  const uploadFileToStorage = async (fileOrBlob, filename = 'upload.jpg') => {
    const formData = new FormData();
    const file = fileOrBlob instanceof File ? fileOrBlob : new File([fileOrBlob], filename, { type: fileOrBlob.type || 'image/jpeg' });
    formData.append('file', file);
    const response = await axios.post(`${API_URL}/api/admin/gallery/upload`, formData, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return `/api/files/${response.data.path}`;
  };

  // Replace image (from file picker) — preserves position in the gallery
  const [replacingId, setReplacingId] = useState(null);
  const replaceInputRef = useRef(null);

  const triggerReplace = (imageId) => {
    setReplacingId(imageId);
    setTimeout(() => replaceInputRef.current?.click(), 0);
  };

  const handleReplaceFile = async (e) => {
    const file = e.target.files?.[0];
    if (replaceInputRef.current) replaceInputRef.current.value = '';
    const targetId = replacingId;
    setReplacingId(null);
    if (!file || !targetId) return;

    setUploading(true);
    setMessage({ type: '', text: '' });
    try {
      const newUrl = await uploadFileToStorage(file, file.name);
      const nextImages = images.map(img => img.id === targetId ? { ...img, url: newUrl } : img);
      setImages(nextImages);
      await axios.put(`${API_URL}/api/admin/gallery`, nextImages, { withCredentials: true });
      setMessage({ type: 'success', text: 'Image remplacée avec succès!' });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setMessage({ type: 'error', text: typeof detail === 'string' ? detail : 'Erreur lors du remplacement' });
    } finally {
      setUploading(false);
    }
  };

  // Crop modal state
  const [cropTarget, setCropTarget] = useState(null); // { id, url }

  const handleCropSave = async (blob) => {
    if (!cropTarget) return;
    try {
      const newUrl = await uploadFileToStorage(blob, `cropped-${Date.now()}.jpg`);
      const nextImages = images.map(img => img.id === cropTarget.id ? { ...img, url: newUrl } : img);
      setImages(nextImages);
      await axios.put(`${API_URL}/api/admin/gallery`, nextImages, { withCredentials: true });
      setMessage({ type: 'success', text: 'Image recadrée avec succès!' });
      setCropTarget(null);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      setMessage({ type: 'error', text: typeof detail === 'string' ? detail : 'Erreur lors du recadrage' });
      setCropTarget(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await axios.put(`${API_URL}/api/admin/gallery`, images, { withCredentials: true });
      setMessage({ type: 'success', text: 'Galerie mise à jour!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-12">Chargement...</div>;

  return (
    <div translate="no" suppressHydrationWarning>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-['Cormorant_Garamond'] text-3xl font-light">Galerie Photos</h2>
        <div className="flex gap-3">
          <input type="file" ref={fileInputRef} onChange={handleUpload} accept="image/*" className="hidden" />
          <input type="file" ref={replaceInputRef} onChange={handleReplaceFile} accept="image/*" className="hidden" />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} variant="outline" className="rounded-none border-black" data-testid="gallery-upload-btn">
            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {uploading ? 'Upload...' : 'Ajouter'}
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-black text-white hover:bg-neutral-800 rounded-none" data-testid="gallery-save-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </div>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {message.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span>{message.text}</span>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {images.map((image) => (
          <div key={image.id} className="relative group bg-white border border-neutral-200 p-2">
            <img src={resolveAssetUrl(image.url)} alt="Gallery" className="w-full aspect-square object-cover grayscale" loading="lazy" />

            {/* Hover overlay with actions */}
            <div className="absolute inset-2 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end justify-center opacity-0 group-hover:opacity-100">
              <div className="flex gap-2 mb-2 flex-wrap justify-center">
                <button
                  onClick={() => triggerReplace(image.id)}
                  disabled={uploading}
                  title="Remplacer"
                  className="bg-white text-black hover:bg-neutral-200 px-3 py-2 text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
                  data-testid={`gallery-replace-${image.id}`}
                >
                  <Replace className="w-3.5 h-3.5" /> Remplacer
                </button>
                <button
                  onClick={() => setCropTarget({ id: image.id, url: image.url })}
                  title="Recadrer"
                  className="bg-white text-black hover:bg-neutral-200 px-3 py-2 text-xs font-medium flex items-center gap-1.5"
                  data-testid={`gallery-crop-${image.id}`}
                >
                  <Crop className="w-3.5 h-3.5" /> Recadrer
                </button>
                <button
                  onClick={() => removeImage(image.id)}
                  title="Supprimer"
                  className="bg-red-500 text-white hover:bg-red-600 px-3 py-2 text-xs font-medium flex items-center gap-1.5"
                  data-testid={`gallery-remove-${image.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Supprimer
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {images.length === 0 && (
        <div className="text-center py-12 bg-white border border-neutral-200">
          <Image className="w-12 h-12 mx-auto text-neutral-300 mb-4" />
          <p className="text-neutral-500">Aucune image dans la galerie</p>
        </div>
      )}

      {cropTarget && (
        <CropModal
          imageUrl={cropTarget.url}
          onClose={() => setCropTarget(null)}
          onSave={handleCropSave}
        />
      )}
    </div>
  );
};

// Services Tab — dynamic CRUD (title, description, image, add/delete, toggle)
const ServicesTab = ({ brand }) => {
  const config = brandConfig[brand] || brandConfig.luxea;
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editing, setEditing] = useState(null); // null | { mode: 'create'|'edit', service }
  const [cropTarget, setCropTarget] = useState(null); // { id, url }
  const replaceInputRef = useRef(null);
  const [replacingId, setReplacingId] = useState(null);

  useEffect(() => { fetchServices(); }, [brand]);

  useEffect(() => {
    if (message.type === 'success') {
      const t = setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const fetchServices = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API_URL}/api/admin/services`, { withCredentials: true });
      setServices(resp.data.services || []);
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors du chargement' });
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async (fileOrBlob, filename = 'service.jpg') => {
    const fd = new FormData();
    const file = fileOrBlob instanceof File ? fileOrBlob : new File([fileOrBlob], filename, { type: fileOrBlob.type || 'image/jpeg' });
    fd.append('file', file);
    const resp = await axios.post(`${API_URL}/api/admin/services/upload-image`, fd, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return `/api/files/${resp.data.path}`;
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Supprimer le service "${title}" ?`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API_URL}/api/admin/services/${id}`, { withCredentials: true });
      setServices(prev => prev.filter(s => s.id !== id));
      setMessage({ type: 'success', text: 'Service supprimé' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de la suppression' });
    } finally {
      setBusy(false);
    }
  };

  const handleToggleActive = async (svc) => {
    setBusy(true);
    try {
      const resp = await axios.put(`${API_URL}/api/admin/services/${svc.id}`, { active: !svc.active }, { withCredentials: true });
      setServices(prev => prev.map(s => s.id === svc.id ? resp.data : s));
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de la mise à jour' });
    } finally {
      setBusy(false);
    }
  };

  const triggerReplace = (id) => {
    setReplacingId(id);
    setTimeout(() => replaceInputRef.current?.click(), 0);
  };

  const handleReplaceFile = async (e) => {
    const file = e.target.files?.[0];
    if (replaceInputRef.current) replaceInputRef.current.value = '';
    const id = replacingId;
    setReplacingId(null);
    if (!file || !id) return;
    setBusy(true);
    try {
      const newUrl = await uploadFile(file, file.name);
      const resp = await axios.put(`${API_URL}/api/admin/services/${id}`, { image_url: newUrl }, { withCredentials: true });
      setServices(prev => prev.map(s => s.id === id ? resp.data : s));
      setMessage({ type: 'success', text: 'Image remplacée' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors du remplacement' });
    } finally {
      setBusy(false);
    }
  };

  const handleCropSave = async (blob) => {
    if (!cropTarget) return;
    setBusy(true);
    try {
      const newUrl = await uploadFile(blob, `cropped-${Date.now()}.jpg`);
      const resp = await axios.put(`${API_URL}/api/admin/services/${cropTarget.id}`, { image_url: newUrl }, { withCredentials: true });
      setServices(prev => prev.map(s => s.id === cropTarget.id ? resp.data : s));
      setMessage({ type: 'success', text: 'Image recadrée' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors du recadrage' });
    } finally {
      setBusy(false);
      setCropTarget(null);
    }
  };

  if (loading) return <div className="text-center py-12">Chargement...</div>;

  return (
    <div translate="no" suppressHydrationWarning>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-['Cormorant_Garamond'] text-3xl font-light">Services</h2>
          <p className="text-sm text-neutral-500 mt-1">{config.name}</p>
        </div>
        <Button
          onClick={() => setEditing({ mode: 'create', service: null })}
          className="bg-black text-white hover:bg-neutral-800 rounded-none"
          data-testid="service-add-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Ajouter un service
        </Button>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {message.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span>{message.text}</span>
        </div>
      )}

      <input type="file" ref={replaceInputRef} onChange={handleReplaceFile} accept="image/*" className="hidden" />

      {services.length === 0 ? (
        <div className="text-center py-16 bg-white border border-neutral-200">
          <Image className="w-12 h-12 mx-auto text-neutral-300 mb-4" />
          <p className="text-neutral-500 mb-4">Aucun service pour le moment</p>
          <p className="text-xs text-neutral-400">Cliquez sur « Ajouter un service » pour créer votre premier service.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {services.map((svc) => (
            <div key={svc.id} className={`bg-white border ${svc.active ? 'border-neutral-200' : 'border-dashed border-neutral-300 opacity-60'} flex flex-col`}>
              <div className="relative group bg-neutral-100">
                {svc.image_url ? (
                  <img src={resolveAssetUrl(svc.image_url)} alt={svc.title} className="w-full aspect-[3/4] object-cover" />
                ) : (
                  <div className="w-full aspect-[3/4] flex items-center justify-center text-neutral-300">
                    <Image className="w-12 h-12" />
                  </div>
                )}
                <div className="absolute top-3 left-3">
                  <span className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${svc.active ? 'bg-black text-white' : 'bg-neutral-500 text-white'}`}>
                    {svc.active ? 'Actif' : 'Inactif'}
                  </span>
                </div>
                {svc.image_url && (
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end justify-center opacity-0 group-hover:opacity-100">
                    <div className="flex gap-1.5 mb-2 flex-wrap justify-center px-2">
                      <button onClick={() => triggerReplace(svc.id)} title="Remplacer" className="bg-white text-black hover:bg-neutral-200 px-2.5 py-1.5 text-[11px] font-medium flex items-center gap-1" data-testid={`service-replace-${svc.id}`}>
                        <Replace className="w-3 h-3" /> Remplacer
                      </button>
                      <button onClick={() => setCropTarget({ id: svc.id, url: svc.image_url })} title="Recadrer" className="bg-white text-black hover:bg-neutral-200 px-2.5 py-1.5 text-[11px] font-medium flex items-center gap-1" data-testid={`service-crop-${svc.id}`}>
                        <Crop className="w-3 h-3" /> Recadrer
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="p-4 flex-1 flex flex-col gap-3">
                <h3 className="font-medium text-base">{svc.title}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed line-clamp-3 min-h-[3.5rem]">
                  {svc.description || <span className="text-neutral-400 italic">Aucune description</span>}
                </p>
                <div className="flex items-center gap-2 mt-auto pt-2 border-t border-neutral-100">
                  <button
                    onClick={() => setEditing({ mode: 'edit', service: svc })}
                    className="flex-1 px-3 py-2 text-xs font-medium border border-black hover:bg-black hover:text-white transition-colors flex items-center justify-center gap-1.5"
                    data-testid={`service-edit-${svc.id}`}
                  >
                    <Edit3 className="w-3.5 h-3.5" /> Modifier
                  </button>
                  <button
                    onClick={() => handleToggleActive(svc)}
                    disabled={busy}
                    title={svc.active ? 'Désactiver' : 'Activer'}
                    className="px-3 py-2 text-xs font-medium border border-neutral-300 hover:border-black transition-colors"
                    data-testid={`service-toggle-${svc.id}`}
                  >
                    {svc.active ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    onClick={() => handleDelete(svc.id, svc.title)}
                    disabled={busy}
                    title="Supprimer"
                    className="px-3 py-2 text-xs font-medium border border-red-300 text-red-600 hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors"
                    data-testid={`service-delete-${svc.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ServiceEditor
          mode={editing.mode}
          service={editing.service}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            if (editing.mode === 'create') {
              setServices(prev => [...prev, saved]);
              setMessage({ type: 'success', text: 'Service créé' });
            } else {
              setServices(prev => prev.map(s => s.id === saved.id ? saved : s));
              setMessage({ type: 'success', text: 'Service mis à jour' });
            }
            setEditing(null);
          }}
          uploadFile={uploadFile}
        />
      )}

      {cropTarget && (
        <CropModal
          imageUrl={cropTarget.url}
          onClose={() => setCropTarget(null)}
          onSave={handleCropSave}
        />
      )}
    </div>
  );
};

// Service Editor Modal (create or edit)
const ServiceEditor = ({ mode, service, onClose, onSaved, uploadFile }) => {
  const [title, setTitle] = useState(service?.title || '');
  const [description, setDescription] = useState(service?.description || '');
  const [imageUrl, setImageUrl] = useState(service?.image_url || '');
  const [active, setActive] = useState(service?.active ?? true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const url = await uploadFile(file, file.name);
      setImageUrl(url);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : "Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError("Le titre est requis.");
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        title: title.trim(),
        description: description,
        image_url: imageUrl,
        active: active,
      };
      let resp;
      if (mode === 'create') {
        resp = await axios.post(`${API_URL}/api/admin/services`, payload, { withCredentials: true });
      } else {
        resp = await axios.put(`${API_URL}/api/admin/services/${service.id}`, payload, { withCredentials: true });
      }
      onSaved(resp.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4 overflow-y-auto" data-testid="service-editor-modal">
      <div className="bg-white w-full max-w-3xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-neutral-200">
          <h3 className="font-['Cormorant_Garamond'] text-2xl font-light">
            {mode === 'create' ? 'Nouveau service' : 'Modifier le service'}
          </h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-black">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2">Image (ratio 3:4)</label>
            <div className="relative bg-neutral-100 border border-neutral-200" style={{ aspectRatio: '3 / 4' }}>
              {imageUrl ? (
                <img src={resolveAssetUrl(imageUrl)} alt="Aperçu" className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-neutral-400">
                  <Image className="w-12 h-12 mb-2" />
                  <span className="text-xs">Aucune image</span>
                </div>
              )}
              {uploading && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              )}
            </div>
            <input type="file" ref={fileRef} onChange={handleFile} accept="image/*" className="hidden" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading} variant="outline" className="w-full mt-3 rounded-none border-black" data-testid="service-image-upload">
              <Upload className="w-4 h-4 mr-2" />
              {imageUrl ? "Changer l'image" : "Uploader l'image"}
            </Button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2">Titre du service *</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex. Cils & Sourcils"
                className="rounded-none"
                data-testid="service-title-input"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2">Description</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex. Extensions de cils, rehaussement, microblading…"
                rows={5}
                className="rounded-none"
                data-testid="service-description-input"
              />
              <p className="text-xs text-neutral-400 mt-1">Court résumé affiché sous le titre du service sur le site.</p>
            </div>

            <label className="flex items-center gap-3 cursor-pointer pt-2">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="w-4 h-4 accent-black"
                data-testid="service-active-toggle"
              />
              <span className="text-sm"><strong>Actif</strong> — visible sur le site public</span>
            </label>
          </div>
        </div>

        {error && (
          <div className="mx-6 mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        <div className="flex justify-end gap-3 p-5 border-t border-neutral-200">
          <Button onClick={onClose} variant="outline" className="rounded-none border-black" data-testid="service-editor-cancel">
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={saving || uploading} className="bg-black text-white hover:bg-neutral-800 rounded-none" data-testid="service-editor-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {mode === 'create' ? 'Créer' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Promotions Tab
const PromotionsTab = ({ brand }) => {
  const config = brandConfig[brand] || brandConfig.luxea;
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editing, setEditing] = useState(null); // null | { mode: 'create'|'edit', promo }
  const [cropTarget, setCropTarget] = useState(null); // { id, url }
  const replaceInputRef = useRef(null);
  const [replacingId, setReplacingId] = useState(null);

  useEffect(() => { fetchPromotions(); }, [brand]);

  useEffect(() => {
    if (message.type === 'success') {
      const t = setTimeout(() => setMessage({ type: '', text: '' }), 3000);
      return () => clearTimeout(t);
    }
  }, [message]);

  const fetchPromotions = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API_URL}/api/admin/promotions`, { withCredentials: true });
      setPromotions(resp.data.promotions || []);
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors du chargement' });
    } finally {
      setLoading(false);
    }
  };

  const uploadFile = async (fileOrBlob, filename = 'promo.jpg') => {
    const fd = new FormData();
    const file = fileOrBlob instanceof File ? fileOrBlob : new File([fileOrBlob], filename, { type: fileOrBlob.type || 'image/jpeg' });
    fd.append('file', file);
    const resp = await axios.post(`${API_URL}/api/admin/promotions/upload`, fd, {
      withCredentials: true,
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return `/api/files/${resp.data.path}`;
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette promotion ?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API_URL}/api/admin/promotions/${id}`, { withCredentials: true });
      setPromotions(prev => prev.filter(p => p.id !== id));
      setMessage({ type: 'success', text: 'Promotion supprimée' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de la suppression' });
    } finally {
      setBusy(false);
    }
  };

  const handleToggleActive = async (promo) => {
    setBusy(true);
    try {
      const resp = await axios.put(`${API_URL}/api/admin/promotions/${promo.id}`, { active: !promo.active }, { withCredentials: true });
      setPromotions(prev => prev.map(p => p.id === promo.id ? resp.data : p));
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors de la mise à jour' });
    } finally {
      setBusy(false);
    }
  };

  const triggerReplace = (id) => {
    setReplacingId(id);
    setTimeout(() => replaceInputRef.current?.click(), 0);
  };

  const handleReplaceFile = async (e) => {
    const file = e.target.files?.[0];
    if (replaceInputRef.current) replaceInputRef.current.value = '';
    const id = replacingId;
    setReplacingId(null);
    if (!file || !id) return;
    setBusy(true);
    try {
      const newUrl = await uploadFile(file, file.name);
      const resp = await axios.put(`${API_URL}/api/admin/promotions/${id}`, { image_url: newUrl }, { withCredentials: true });
      setPromotions(prev => prev.map(p => p.id === id ? resp.data : p));
      setMessage({ type: 'success', text: 'Image remplacée' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors du remplacement' });
    } finally {
      setBusy(false);
    }
  };

  const handleCropSave = async (blob) => {
    if (!cropTarget) return;
    setBusy(true);
    try {
      const newUrl = await uploadFile(blob, `cropped-${Date.now()}.jpg`);
      const resp = await axios.put(`${API_URL}/api/admin/promotions/${cropTarget.id}`, { image_url: newUrl }, { withCredentials: true });
      setPromotions(prev => prev.map(p => p.id === cropTarget.id ? resp.data : p));
      setMessage({ type: 'success', text: 'Image recadrée' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur lors du recadrage' });
    } finally {
      setBusy(false);
      setCropTarget(null);
    }
  };

  if (loading) return <div className="text-center py-12">Chargement...</div>;

  return (
    <div translate="no" suppressHydrationWarning>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-['Cormorant_Garamond'] text-3xl font-light">Promotions en cours</h2>
          <p className="text-sm text-neutral-500 mt-1">{config.name}</p>
        </div>
        <Button
          onClick={() => setEditing({ mode: 'create', promo: null })}
          className="bg-black text-white hover:bg-neutral-800 rounded-none"
          data-testid="promo-add-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Ajouter une affiche
        </Button>
      </div>

      {message.text && (
        <div className={`mb-6 p-4 flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {message.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span>{message.text}</span>
        </div>
      )}

      <input type="file" ref={replaceInputRef} onChange={handleReplaceFile} accept="image/*" className="hidden" />

      {promotions.length === 0 ? (
        <div className="text-center py-16 bg-white border border-neutral-200">
          <Sparkles className="w-12 h-12 mx-auto text-neutral-300 mb-4" />
          <p className="text-neutral-500 mb-4">Aucune promotion pour le moment</p>
          <p className="text-xs text-neutral-400">Cliquez sur « Ajouter une affiche » pour créer votre première promotion.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {promotions.map((promo) => (
            <div key={promo.id} className={`bg-white border ${promo.active ? 'border-neutral-200' : 'border-dashed border-neutral-300 opacity-60'} flex flex-col`}>
              <div className="relative group bg-neutral-100">
                <img src={resolveAssetUrl(promo.image_url)} alt="Promotion" className="w-full aspect-[3/4] object-cover" />
                {/* Active/inactive badge */}
                <div className="absolute top-3 left-3">
                  <span className={`px-2 py-1 text-[10px] font-semibold uppercase tracking-wider ${promo.active ? 'bg-black text-white' : 'bg-neutral-500 text-white'}`}>
                    {promo.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                {/* Hover actions */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex gap-1.5 mb-2 flex-wrap justify-center px-2">
                    <button onClick={() => triggerReplace(promo.id)} title="Remplacer" className="bg-white text-black hover:bg-neutral-200 px-2.5 py-1.5 text-[11px] font-medium flex items-center gap-1" data-testid={`promo-replace-${promo.id}`}>
                      <Replace className="w-3 h-3" /> Remplacer
                    </button>
                    <button onClick={() => setCropTarget({ id: promo.id, url: promo.image_url })} title="Recadrer" className="bg-white text-black hover:bg-neutral-200 px-2.5 py-1.5 text-[11px] font-medium flex items-center gap-1" data-testid={`promo-crop-${promo.id}`}>
                      <Crop className="w-3 h-3" /> Recadrer
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-4 flex-1 flex flex-col gap-3">
                <p className="text-sm text-neutral-700 leading-relaxed line-clamp-3 min-h-[3.5rem]">
                  {promo.description || <span className="text-neutral-400 italic">Aucune description</span>}
                </p>
                {promo.booking_link && (
                  <a href={promo.booking_link} target="_blank" rel="noopener noreferrer" className="text-xs text-black hover:underline inline-flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> Lien de réservation
                  </a>
                )}
                <div className="flex items-center gap-2 mt-auto pt-2 border-t border-neutral-100">
                  <button
                    onClick={() => setEditing({ mode: 'edit', promo })}
                    className="flex-1 px-3 py-2 text-xs font-medium border border-black hover:bg-black hover:text-white transition-colors flex items-center justify-center gap-1.5"
                    data-testid={`promo-edit-${promo.id}`}
                  >
                    <Edit3 className="w-3.5 h-3.5" /> Modifier
                  </button>
                  <button
                    onClick={() => handleToggleActive(promo)}
                    disabled={busy}
                    title={promo.active ? 'Désactiver' : 'Activer'}
                    className="px-3 py-2 text-xs font-medium border border-neutral-300 hover:border-black transition-colors flex items-center gap-1.5"
                    data-testid={`promo-toggle-${promo.id}`}
                  >
                    {promo.active ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    onClick={() => handleDelete(promo.id)}
                    disabled={busy}
                    title="Supprimer"
                    className="px-3 py-2 text-xs font-medium border border-red-300 text-red-600 hover:bg-red-500 hover:text-white hover:border-red-500 transition-colors"
                    data-testid={`promo-delete-${promo.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <PromotionEditor
          mode={editing.mode}
          promo={editing.promo}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            if (editing.mode === 'create') {
              setPromotions(prev => [...prev, saved]);
              setMessage({ type: 'success', text: 'Promotion créée' });
            } else {
              setPromotions(prev => prev.map(p => p.id === saved.id ? saved : p));
              setMessage({ type: 'success', text: 'Promotion mise à jour' });
            }
            setEditing(null);
          }}
          uploadFile={uploadFile}
        />
      )}

      {cropTarget && (
        <CropModal
          imageUrl={cropTarget.url}
          onClose={() => setCropTarget(null)}
          onSave={handleCropSave}
        />
      )}
    </div>
  );
};

// Promotion Editor Modal (create or edit)
const PromotionEditor = ({ mode, promo, onClose, onSaved, uploadFile }) => {
  const [imageUrl, setImageUrl] = useState(promo?.image_url || '');
  const [description, setDescription] = useState(promo?.description || '');
  const [bookingLink, setBookingLink] = useState(promo?.booking_link || '');
  const [active, setActive] = useState(promo?.active ?? true);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const url = await uploadFile(file, file.name);
      setImageUrl(url);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : "Erreur lors de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!imageUrl) {
      setError("Veuillez d'abord uploader une image.");
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        image_url: imageUrl,
        description: description,
        booking_link: bookingLink || null,
        active: active,
      };
      let resp;
      if (mode === 'create') {
        resp = await axios.post(`${API_URL}/api/admin/promotions`, payload, { withCredentials: true });
      } else {
        resp = await axios.put(`${API_URL}/api/admin/promotions/${promo.id}`, payload, { withCredentials: true });
      }
      onSaved(resp.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4 overflow-y-auto" data-testid="promo-editor-modal">
      <div className="bg-white w-full max-w-3xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-neutral-200">
          <h3 className="font-['Cormorant_Garamond'] text-2xl font-light">
            {mode === 'create' ? 'Nouvelle promotion' : 'Modifier la promotion'}
          </h3>
          <button onClick={onClose} className="text-neutral-500 hover:text-black">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 grid md:grid-cols-2 gap-6">
          {/* Image side */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2">Affiche (ratio 3:4)</label>
            <div className="relative bg-neutral-100 border border-neutral-200" style={{ aspectRatio: '3 / 4' }}>
              {imageUrl ? (
                <img src={resolveAssetUrl(imageUrl)} alt="Aperçu" className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-neutral-400">
                  <Image className="w-12 h-12 mb-2" />
                  <span className="text-xs">Aucune image</span>
                </div>
              )}
              {uploading && (
                <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                  <Loader2 className="w-6 h-6 animate-spin" />
                </div>
              )}
            </div>
            <input type="file" ref={fileRef} onChange={handleFile} accept="image/*" className="hidden" />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              variant="outline"
              className="w-full mt-3 rounded-none border-black"
              data-testid="promo-image-upload"
            >
              <Upload className="w-4 h-4 mr-2" />
              {imageUrl ? "Changer l'image" : "Uploader l'image"}
            </Button>
            <p className="text-xs text-neutral-400 mt-2">
              Format recommandé : portrait 3:4 (ex. 900×1200px). Vous pourrez recadrer ensuite depuis la liste.
            </p>
          </div>

          {/* Fields side */}
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2">Description courte</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Ex. -20% sur la pose complète d'extensions ce mois-ci."
                rows={4}
                className="rounded-none"
                data-testid="promo-description-input"
              />
              <p className="text-xs text-neutral-400 mt-1">Affichée sous l'image. Gardez court (1–2 phrases).</p>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2">Lien de réservation (optionnel)</label>
              <Input
                value={bookingLink}
                onChange={(e) => setBookingLink(e.target.value)}
                placeholder="https://..."
                className="rounded-none"
                data-testid="promo-booking-input"
              />
            </div>

            <label className="flex items-center gap-3 cursor-pointer pt-2">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="w-4 h-4 accent-black"
                data-testid="promo-active-toggle"
              />
              <span className="text-sm">
                <strong>Active</strong> — visible sur le site public
              </span>
            </label>
          </div>
        </div>

        {error && (
          <div className="mx-6 mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        <div className="flex justify-end gap-3 p-5 border-t border-neutral-200">
          <Button onClick={onClose} variant="outline" className="rounded-none border-black" data-testid="promo-editor-cancel">
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={saving || uploading} className="bg-black text-white hover:bg-neutral-800 rounded-none" data-testid="promo-editor-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {mode === 'create' ? 'Créer' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Policies Tab — edit brand-scoped appointment/cancellation policy
const PoliciesTab = ({ brand }) => {
  const [policy, setPolicy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/policy`, { withCredentials: true });
        if (!mounted) return;
        const d = res.data || {};
        setPolicy({
          label: d.label || 'Conditions',
          title: d.title || '',
          intro: d.intro || '',
          sections: Array.isArray(d.sections) ? d.sections.map(s => ({ id: s.id, title: s.title || '', content: s.content || '' })) : [],
          highlight_title: d.highlight_title || '',
          highlight_content: d.highlight_content || '',
          highlight_footer: d.highlight_footer || '',
        });
      } catch (e) {
        setMessage({ type: 'error', text: 'Erreur lors du chargement de la politique.' });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
    // brand change forces remount via key in renderTab — but reset anyway
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand]);

  const update = (patch) => setPolicy(p => ({ ...p, ...patch }));

  const updateSection = (idx, patch) => {
    setPolicy(p => {
      const sections = [...p.sections];
      sections[idx] = { ...sections[idx], ...patch };
      return { ...p, sections };
    });
  };

  const addSection = () => {
    setPolicy(p => ({
      ...p,
      sections: [...p.sections, { id: undefined, title: 'Nouvelle section', content: '' }]
    }));
  };

  const removeSection = (idx) => {
    if (!window.confirm('Supprimer cette section ?')) return;
    setPolicy(p => {
      const sections = [...p.sections];
      sections.splice(idx, 1);
      return { ...p, sections };
    });
  };

  const moveSection = (idx, dir) => {
    setPolicy(p => {
      const sections = [...p.sections];
      const target = idx + dir;
      if (target < 0 || target >= sections.length) return p;
      [sections[idx], sections[target]] = [sections[target], sections[idx]];
      return { ...p, sections };
    });
  };

  const handleSave = async () => {
    if (!policy.title || !policy.title.trim()) {
      setMessage({ type: 'error', text: 'Le titre est obligatoire.' });
      return;
    }
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      const res = await axios.put(`${API_URL}/api/admin/policy`, policy, { withCredentials: true });
      const d = res.data || {};
      setPolicy({
        label: d.label || 'Conditions',
        title: d.title || '',
        intro: d.intro || '',
        sections: Array.isArray(d.sections) ? d.sections.map(s => ({ id: s.id, title: s.title || '', content: s.content || '' })) : [],
        highlight_title: d.highlight_title || '',
        highlight_content: d.highlight_content || '',
        highlight_footer: d.highlight_footer || '',
      });
      setMessage({ type: 'success', text: 'Politique mise à jour avec succès!' });
    } catch (e) {
      setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-12">Chargement...</div>;
  if (!policy) return <div className="text-center py-12 text-red-600">Politique introuvable.</div>;

  return (
    <div data-testid="policies-tab">
      <div className="flex items-center justify-between mb-2">
        <h2 className="font-['Cormorant_Garamond'] text-3xl font-light">Politiques de rendez-vous</h2>
        <Button onClick={handleSave} disabled={saving} className="bg-black text-white hover:bg-neutral-800 rounded-none" data-testid="save-policy-button">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>
      <p className="text-sm text-neutral-500 mb-6">
        Conseil : utilisez <code className="px-1 bg-neutral-100">**texte**</code> pour mettre en gras et une ligne vide pour créer un nouveau paragraphe.
      </p>

      {message.text && (
        <div className={`mb-6 p-4 flex items-center gap-3 ${message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
          {message.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          {message.text}
        </div>
      )}

      {/* Header info */}
      <div className="bg-white border border-neutral-200 p-6 mb-6 space-y-4">
        <h3 className="font-medium text-sm uppercase tracking-wider text-neutral-500">En-tête</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Étiquette (au-dessus du titre)</label>
            <Input
              value={policy.label}
              onChange={(e) => update({ label: e.target.value })}
              placeholder="Conditions"
              className="rounded-none"
              data-testid="policy-label-input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Titre principal *</label>
            <Input
              value={policy.title}
              onChange={(e) => update({ title: e.target.value })}
              placeholder="Politique d'annulation et de retard"
              className="rounded-none"
              data-testid="policy-title-input"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Introduction</label>
          <Textarea
            value={policy.intro}
            onChange={(e) => update({ intro: e.target.value })}
            placeholder="Phrase d'introduction (facultatif)"
            className="rounded-none min-h-[70px]"
            data-testid="policy-intro-input"
          />
        </div>
      </div>

      {/* Sections */}
      <div className="bg-white border border-neutral-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-sm uppercase tracking-wider text-neutral-500">Sections ({policy.sections.length})</h3>
          <Button onClick={addSection} variant="outline" className="rounded-none" data-testid="add-section-button">
            <Plus className="w-4 h-4 mr-2" /> Ajouter une section
          </Button>
        </div>
        {policy.sections.length === 0 ? (
          <p className="text-sm text-neutral-500 italic">Aucune section. Cliquez sur « Ajouter une section ».</p>
        ) : (
          <div className="space-y-4">
            {policy.sections.map((s, idx) => (
              <div key={s.id || `new-${idx}`} className="border border-neutral-200 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-medium text-neutral-400 w-6">#{idx + 1}</span>
                  <Input
                    value={s.title}
                    onChange={(e) => updateSection(idx, { title: e.target.value })}
                    placeholder="Titre de la section"
                    className="rounded-none flex-1"
                    data-testid={`section-title-${idx}`}
                  />
                  <button
                    onClick={() => moveSection(idx, -1)}
                    disabled={idx === 0}
                    className="p-2 text-neutral-500 hover:text-black disabled:opacity-30"
                    title="Monter"
                    data-testid={`section-up-${idx}`}
                  >
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => moveSection(idx, +1)}
                    disabled={idx === policy.sections.length - 1}
                    className="p-2 text-neutral-500 hover:text-black disabled:opacity-30"
                    title="Descendre"
                    data-testid={`section-down-${idx}`}
                  >
                    <ArrowDown className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => removeSection(idx)}
                    className="p-2 text-red-500 hover:text-red-700"
                    title="Supprimer"
                    data-testid={`section-delete-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <Textarea
                  value={s.content}
                  onChange={(e) => updateSection(idx, { content: e.target.value })}
                  placeholder="Contenu de la section. Utilisez **texte** pour gras."
                  className="rounded-none min-h-[110px]"
                  data-testid={`section-content-${idx}`}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Highlight box */}
      <div className="bg-white border border-neutral-200 p-6 space-y-4">
        <h3 className="font-medium text-sm uppercase tracking-wider text-neutral-500">Encadré (bandeau noir en bas)</h3>
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Titre de l'encadré</label>
          <Input
            value={policy.highlight_title}
            onChange={(e) => update({ highlight_title: e.target.value })}
            placeholder="Ex: Dépôt et réservation (facultatif)"
            className="rounded-none"
            data-testid="policy-highlight-title-input"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Contenu de l'encadré</label>
          <Textarea
            value={policy.highlight_content}
            onChange={(e) => update({ highlight_content: e.target.value })}
            placeholder="Contenu (facultatif). Utilisez **texte** pour gras."
            className="rounded-none min-h-[100px]"
            data-testid="policy-highlight-content-input"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">Pied de l'encadré</label>
          <Input
            value={policy.highlight_footer}
            onChange={(e) => update({ highlight_footer: e.target.value })}
            placeholder="Ex: En prenant rendez-vous, vous acceptez cette politique. (facultatif)"
            className="rounded-none"
            data-testid="policy-highlight-footer-input"
          />
        </div>
      </div>
    </div>
  );
};



// Main Admin Dashboard Component
const AdminDashboard = () => {
  const { brand } = useParams();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const { user, loading, logout, checkAuth } = useAuth();
  const navigate = useNavigate();

  const currentBrand = brand || 'luxea';

  // Force a fresh auth check whenever the dashboard mounts or the URL brand changes.
  // This eliminates stale session/state when the user has switched between admins.
  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBrand]);

  useEffect(() => {
    if (!loading && !user) {
      navigate(`/admin/${currentBrand}`);
      return;
    }
    // If the logged-in admin's brand doesn't match the URL, redirect to their own dashboard
    if (!loading && user && user.brand && user.brand !== currentBrand) {
      navigate(`/admin/${user.brand}/dashboard`, { replace: true });
    }
  }, [user, loading, navigate, currentBrand]);

  // Global axios interceptor: if any admin call returns 403 "Service non autorisé"
  // or similar brand mismatch, force logout to clear the stale session.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (resp) => resp,
      async (error) => {
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;
        const url = error?.config?.url || '';
        if (status === 403 && url.includes('/api/admin/') && typeof detail === 'string' && detail.toLowerCase().includes('marque')) {
          await logout();
          alert("Votre session ne correspond pas à cette marque. Veuillez vous reconnecter.");
          navigate(`/admin/${currentBrand}`);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [logout, navigate, currentBrand]);

  const handleLogout = async () => {
    await logout();
    navigate(`/admin/${currentBrand}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-neutral-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  const renderTab = () => {
    switch (activeTab) {
      case 'pricing': return <PricingTab brand={currentBrand} />;
      case 'hours': return <HoursTab />;
      case 'gallery': return <GalleryTab />;
      case 'services': return <ServicesTab brand={currentBrand} />;
      case 'promotions': return <PromotionsTab brand={currentBrand} />;
      case 'policies': return <PoliciesTab brand={currentBrand} />;
      default: return <DashboardTab brand={currentBrand} setActiveTab={setActiveTab} />;
    }
  };

  return (
    <div className="min-h-screen bg-neutral-100 flex" data-testid="admin-dashboard">
      <Sidebar
        brand={currentBrand}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onLogout={handleLogout}
        isMobileOpen={isMobileOpen}
        setIsMobileOpen={setIsMobileOpen}
      />

      <main className="flex-1 min-h-screen">
        <header className="bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between">
          <button className="lg:hidden p-2 text-black" onClick={() => setIsMobileOpen(true)}>
            <Menu className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-4">
            <span className={`px-3 py-1 text-xs font-medium tracking-wider uppercase ${user.brand === 'luxea' ? 'bg-black text-white' : 'bg-neutral-800 text-white'}`} data-testid="brand-badge">
              {user.brand === 'coiffure' ? 'Coiffure YT' : 'Luxéa Esthétique'}
            </span>
            <span className="text-sm text-neutral-600 hidden sm:inline">
              Connecté en tant que <strong>{user.name || user.email}</strong>
            </span>
          </div>
        </header>

        <div className="p-6 md:p-8">
          {renderTab()}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
